/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import './PullRequest.css';

import type {GitHubPullRequestParams} from './jotai';

import CenteredSpinner from './CenteredSpinner';
import DiffView from './DiffView';
import PullRequestChangeCount from './PullRequestChangeCount';
import PullRequestLabels from './PullRequestLabels';
import PullRequestReviewers from './PullRequestReviewers';
import PullRequestSignals from './PullRequestSignals';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import {stripStackInfoFromBodyHTML} from './ghstackUtils';
import {
  gitHubOrgAndRepoAtom,
  gitHubPullRequestAtom,
  gitHubPullRequestComparableVersionsAtom,
  gitHubPullRequestForParamsAtom,
  gitHubPullRequestIDAtom,
  gitHubPullRequestVersionDiffAtom,
  pendingScrollRestoreAtom,
  stackedPullRequestAtom,
  fileViewedOverridesAtom,
} from './jotai';
import {stripStackInfoFromSaplingBodyHTML} from './saplingStack';
import {Box, Text} from '@primer/react';
import structuralShare from './structuralShare';
import {useAtomValue, useSetAtom, useStore} from 'jotai';
import {unwrap} from 'jotai/utils';
import {Suspense, useEffect, useMemo, useRef} from 'react';

// CRITICAL: unwrap() must be created at module level, not inline in components.
// Creating it inline causes "Rendered more hooks" errors.
const unwrappedStackedPullRequestAtom = unwrap(stackedPullRequestAtom, prev => prev ?? {type: 'no-stack' as const});
const unwrappedVersionDiffAtom = unwrap(gitHubPullRequestVersionDiffAtom, prev => prev ?? null);

export default function PullRequest() {
  // Note: comparableVersions sync is handled by JotaiRecoilSync component
  // which properly waits for valid data before syncing

  return (
    <Suspense fallback={<CenteredSpinner />}>
      <div className="PullRequest-container">
        <PullRequestBootstrap />
      </div>
    </Suspense>
  );
}

function PullRequestBootstrap() {
  const number = useAtomValue(gitHubPullRequestIDAtom);
  const orgAndRepo = useAtomValue(gitHubOrgAndRepoAtom);
  if (number != null && orgAndRepo != null) {
    return <PullRequestWithParams params={{orgAndRepo, number}} />;
  } else {
    return <Text>This is not a URL for a pull request.</Text>;
  }
}

function PullRequestWithParams({params}: {params: GitHubPullRequestParams}) {
  // Use unwrap to avoid suspending - we want to show the current PR while
  // refreshing in the background. Returns undefined while loading, null if not found.
  const unwrappedPRAtom = useMemo(
    () => unwrap(gitHubPullRequestForParamsAtom(params)),
    [params],
  );
  const pullRequestResult = useAtomValue(unwrappedPRAtom);
  const setPullRequestJotai = useSetAtom(gitHubPullRequestAtom);
  const store = useStore();
  const setComparableVersions = useSetAtom(gitHubPullRequestComparableVersionsAtom);
  const setPendingScrollRestore = useSetAtom(pendingScrollRestoreAtom);
  const setFileViewedOverrides = useSetAtom(fileViewedOverridesAtom);
  const pullRequest = pullRequestResult ?? null;
  const isPullRequestNotFound = pullRequestResult === null;

  // Reset stale atoms when the PR number changes OR on first mount.
  // On fresh mount (e.g., navigating from the home page), the global atoms
  // may still hold data from a previously viewed PR. Without resetting,
  // the diff atoms will try to compare the old PR's commit IDs against the
  // new PR's repo, causing 404s from the compare endpoint.
  const currentNumber = params.number;
  const prevNumberRef = useRef<number | null>(null);
  if (prevNumberRef.current !== currentNumber) {
    setComparableVersions(null);
    setPullRequestJotai(null);
    setFileViewedOverrides(new Map());
  }
  prevNumberRef.current = currentNumber;

  useEffect(() => {
    if (pullRequest != null) {
      // Structurally share with the existing PR data so that unchanged
      // sub-objects keep their references. This prevents downstream atoms
      // and React components from re-evaluating/re-rendering when only a
      // small part of the PR changed (e.g., a new comment was added).
      const existing = store.get(gitHubPullRequestAtom);
      const shared = existing != null
        ? structuralShare(existing, pullRequest)
        : pullRequest;
      setPullRequestJotai(shared);
    }
  }, [pullRequest, setPullRequestJotai, store]);

  // Restore scroll position after pull request data updates.
  // This runs after the effect above updates the atoms, and uses
  // double requestAnimationFrame to wait for React to commit the render
  // and the browser to paint.
  useEffect(() => {
    if (pullRequest != null) {
      // Use double requestAnimationFrame to ensure we restore scroll after
      // React has committed updates AND the browser has finished painting.
      // The first RAF waits for the next frame, the second ensures paint completion.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPendingScrollRestore(prev => {
            if (prev != null) {
              window.scrollTo(prev.scrollX, prev.scrollY);
            }
            return null;
          });
        });
      });
    }
  }, [pullRequest, setPendingScrollRestore]);

  if (isPullRequestNotFound) {
    return <PullRequestNotFound />;
  } else {
    return <PullRequestDetails />;
  }
}

function PullRequestNotFound() {
  return <Text>The specified pull request could not be found.</Text>;
}

function PullRequestDetails() {
  const pullRequest = useAtomValue(gitHubPullRequestAtom);
  // Use loadable to avoid suspending on refresh — when a comment is submitted,
  // the PR data updates, which causes stackedPullRequestAtom to re-evaluate
  // (new promise). Without loadable, this suspends and the outer Suspense
  // boundary replaces the entire page with a spinner.
  // On initial load, pullRequest is null so we return null before reaching
  // the stack read. On refresh, we keep showing the previous stack value.
  // unwrap with fallback retains the previous value while re-evaluating,
  // avoiding suspension on PR refresh (e.g., after comment submission).
  const stack = useAtomValue(unwrappedStackedPullRequestAtom);
  if (pullRequest == null) {
    return null;
  }

  const {bodyHTML} = pullRequest;
  let pullRequestBodyHTML;
  switch (stack.type) {
    case 'no-stack':
    case 'manual':
      pullRequestBodyHTML = bodyHTML;
      break;
    case 'sapling':
      pullRequestBodyHTML = stripStackInfoFromSaplingBodyHTML(bodyHTML, stack.body.format);
      break;
    case 'ghstack':
      pullRequestBodyHTML = stripStackInfoFromBodyHTML(bodyHTML);
      break;
  }

  return (
    <Box display="flex" flexDirection="column" paddingTop={3} gridGap={3}>
      {/* Phabricator-style structured fields */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            bg: '#34495e',
            color: '#ecf0f1',
            px: 3,
            py: 2,
            fontSize: '13px',
            fontWeight: 'bold',
            borderBottom: '1px solid',
            borderColor: 'border.default',
          }}>
          Revision Details
        </Box>
        <Box sx={{bg: 'canvas.default'}}>
          <PhabField label="Summary">
            <Box fontSize={14}>
              <TrustedRenderedMarkdown trustedHTML={pullRequestBodyHTML} />
            </Box>
          </PhabField>
          <PhabField label="Reviewers">
            <PullRequestReviewers />
          </PhabField>
          <PhabField label="Tags">
            <PullRequestLabels />
          </PhabField>
          <PhabField label="Signals">
            <PullRequestSignals />
          </PhabField>
        </Box>
      </Box>

      <Suspense fallback={<CenteredSpinner />}>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '.5rem',
              paddingBottom: '.5rem',
            }}>
            <PullRequestChangeCount />
          </div>
          <PullRequestVersionDiff />
        </div>
      </Suspense>
    </Box>
  );
}

function PhabField({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <Box
      sx={{
        display: 'flex',
        borderBottom: '1px solid',
        borderColor: 'border.subtle',
        ':last-child': {borderBottom: 'none'},
      }}>
      <Box
        sx={{
          width: '120px',
          flexShrink: 0,
          px: 3,
          py: 2,
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'fg.muted',
          bg: 'canvas.subtle',
          borderRight: '1px solid',
          borderColor: 'border.subtle',
        }}>
        {label}
      </Box>
      <Box sx={{flex: 1, px: 3, py: 2, minWidth: 0}}>{children}</Box>
    </Box>
  );
}

function PullRequestVersionDiff() {
  const diff = useAtomValue(unwrappedVersionDiffAtom);
  if (diff != null) {
    return (
      <Suspense
        fallback={<CenteredSpinner message={'Loading ' + diff.diff.length + ' changes...'} />}>
        <DiffView diff={diff.diff} isPullRequest={true} />
      </Suspense>
    );
  } else {
    return null;
  }
}
